import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Grid
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Cake as CakeIcon } from '@mui/icons-material';
import dayjs from 'dayjs';

const AnniversariesView = ({ events, onEdit, onDelete }) => {
  // Get all anniversary events
  const anniversaryEvents = useMemo(() => {
    return events.filter(event => event.isAnniversary);
  }, [events]);

  // Calculate upcoming anniversaries for the next 12 months
  const upcomingAnniversaries = useMemo(() => {
    const now = dayjs();
    const oneYearFromNow = now.add(12, 'months');
    const anniversaries = [];

    anniversaryEvents.forEach(event => {
      if (!event.eventDate) return;

      const originalDate = dayjs(event.eventDate);
      const currentYear = now.year();
      const originalYear = originalDate.year();

      // Calculate anniversaries for current year and next year
      for (let year = currentYear; year <= currentYear + 1; year++) {
        const anniversaryDate = originalDate.year(year);
        
        // Only include if it's in the next 12 months
        if (anniversaryDate.isAfter(now.subtract(1, 'day')) && anniversaryDate.isBefore(oneYearFromNow)) {
          const yearsSince = year - originalYear;
          anniversaries.push({
            ...event,
            anniversaryDate: anniversaryDate.toDate(),
            yearsSince: yearsSince,
            isThisYear: year === currentYear
          });
        }
      }
    });

    // Sort by date
    return anniversaries.sort((a, b) => {
      return new Date(a.anniversaryDate) - new Date(b.anniversaryDate);
    });
  }, [anniversaryEvents]);

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return dayjs(date).format('MMM DD, YYYY');
  };

  const getDaysUntil = (date) => {
    const days = dayjs(date).diff(dayjs(), 'days');
    if (days < 0) return 'Past';
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    return `${days} days`;
  };

  if (anniversaryEvents.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CakeIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Anniversary Events
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Mark events as "Recurring Anniversary" to see them here yearly
        </Typography>
      </Box>
    );
  }

  if (upcomingAnniversaries.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body1" color="text.secondary">
          No upcoming anniversaries in the next 12 months
        </Typography>
      </Box>
    );
  }

  // Group by month
  const anniversariesByMonth = upcomingAnniversaries.reduce((acc, ann) => {
    const month = dayjs(ann.anniversaryDate).format('MMMM YYYY');
    if (!acc[month]) {
      acc[month] = [];
    }
    acc[month].push(ann);
    return acc;
  }, {});

  return (
    <Box>
      {Object.entries(anniversariesByMonth).map(([month, anniversaries]) => (
        <Box key={month} sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2 }}>
            {month}
          </Typography>
          <Grid container spacing={2}>
            {anniversaries.map((anniversary) => {
              const daysUntil = getDaysUntil(anniversary.anniversaryDate);
              const isUpcoming = daysUntil !== 'Past';
              
              return (
                <Grid item xs={12} sm={6} md={4} key={`${anniversary.id}-${anniversary.anniversaryDate}`}>
                  <Card 
                    elevation={isUpcoming && daysUntil === 'Today' ? 4 : 2}
                    sx={{
                      height: '100%',
                      border: isUpcoming && daysUntil === 'Today' ? 2 : 0,
                      borderColor: isUpcoming && daysUntil === 'Today' ? 'primary.main' : 'transparent'
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" component="h3" gutterBottom>
                            {anniversary.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {formatDate(anniversary.anniversaryDate)}
                          </Typography>
                          {anniversary.yearsSince > 0 && (
                            <Chip
                              label={`${anniversary.yearsSince} ${anniversary.yearsSince === 1 ? 'year' : 'years'}`}
                              size="small"
                              color="primary"
                              sx={{ mt: 0.5 }}
                            />
                          )}
                        </Box>
                        <Box>
                          <IconButton
                            size="small"
                            onClick={() => onEdit(anniversary)}
                            color="primary"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => onDelete(anniversary.id)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                      
                      <Chip
                        label={anniversary.category || 'N/A'}
                        size="small"
                        color={anniversary.category === 'work' ? 'primary' : 'secondary'}
                        sx={{ mb: 1 }}
                      />
                      
                      {isUpcoming && (
                        <Typography 
                          variant="body2" 
                          color={daysUntil === 'Today' ? 'primary.main' : 'text.secondary'}
                          fontWeight={daysUntil === 'Today' ? 'bold' : 'normal'}
                        >
                          {daysUntil === 'Today' ? '🎉 Today!' : `${daysUntil} away`}
                        </Typography>
                      )}
                      
                      {anniversary.location && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                          📍 {anniversary.location}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      ))}
    </Box>
  );
};

export default AnniversariesView;





